/**
 * The month rail (concept C, "Bill first"): the current period first, then
 * every past invoice, newest first. Mirrors `SettingsRail` (active row
 * `border-l-threadheads-purple` + `bg-primary/10`), with real list semantics:
 * a `<nav>` of `<li><button aria-current>` (never a button re-roled as a list
 * item, which would lose its button semantics).
 *
 * Each row: the period by Stripe's real dates ("30 Sep to 30 Oct 2026",
 * never a calendar month name), the amount ex GST in the bill's currency
 * (the rail's header says "ex GST" once, so twelve rows don't repeat it), the
 * piece count when there is one, and the state in a `StatusPill`.
 *
 * lg and up: a 260px column that stays in view while the bill scrolls, its
 * list scrolling inside when there are many months. Below lg: a strip above
 * the bill that scrolls sideways (the SettingsRail precedent), so two years
 * of invoices never push the bill a screen down.
 *
 * Past months load on their own: a failed read is an inline error with
 * Retry under the current row (never an empty list that reads as "no
 * invoices"), and the current month still works.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { cn } from '@/lib/utils';
import { useBillingFormat } from '../hooks/useBillingFormat';
import { CARD_CLASS } from '../lib/billing-ui';
import type { MonthEntry, MonthRail } from '../lib/billing-view';
import { BillingStatusPill } from './BillingStatusPill';

interface BillingMonthRailProps {
  rail: MonthRail;
  activeToken: string;
  onSelect: (token: string) => void;
  months: { state: 'loading' | 'error' | 'ready'; retry: () => void; retrying: boolean };
}

const ROW_CLASS =
  'flex w-52 shrink-0 snap-start flex-col gap-1 rounded-md border-l-2 px-2.5 py-2 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none lg:w-full';

export function BillingMonthRail({ rail, activeToken, onSelect, months }: BillingMonthRailProps) {
  const { t } = useTranslation('billing');
  const fmt = useBillingFormat();
  const titleId = useId();

  const label = (entry: MonthEntry): string => {
    if (entry.period) return fmt.period(entry.period);
    return entry.kind === 'invoice' ? fmt.day(entry.month.date) : '';
  };

  const row = (entry: MonthEntry) => {
    const active = entry.token === activeToken;
    return (
      <li key={entry.token} className="flex lg:block">
        <button
          type="button"
          onClick={() => onSelect(entry.token)}
          aria-current={active ? 'true' : undefined}
          className={cn(
            ROW_CLASS,
            active
              ? 'border-l-threadheads-purple bg-primary/10'
              : 'border-transparent hover:bg-muted/60',
          )}
        >
          <span className="flex w-full items-baseline justify-between gap-2">
            <span className="min-w-0 text-sm font-medium tabular-nums">{label(entry)}</span>
            <span className="shrink-0 text-sm tabular-nums">{fmt.money(entry.amountCents, entry.currency)}</span>
          </span>
          <span className="flex w-full items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground tabular-nums">
              {entry.pieces !== null ? t('count.pieces', { count: entry.pieces }) : null}
            </span>
            <BillingStatusPill status={entry.status} />
          </span>
        </button>
      </li>
    );
  };

  const first = rail.first;
  const firstActive = first !== null && first.token === activeToken;

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        CARD_CLASS,
        'min-w-0 lg:sticky lg:top-[calc(var(--top-header-height,3.5rem)_+_1rem)]',
      )}
    >
      <header className="flex items-baseline justify-between gap-2 border-b px-3 py-2.5">
        <h3 id={titleId} className="text-sm font-semibold">
          {t('rail.title')}
        </h3>
        <p className="text-xs text-muted-foreground">{t('rail.description')}</p>
      </header>

      <nav aria-label={t('rail.label')}>
        <ul className="flex snap-x gap-1 overflow-x-auto p-1.5 lg:max-h-[60vh] lg:flex-col lg:gap-0.5 lg:overflow-y-auto lg:overflow-x-visible">
          {row(rail.current)}
          {rail.past.map(row)}
          {months.state === 'loading' &&
            [0, 1, 2].map((i) => (
              <li key={`skeleton-${i}`} aria-hidden="true" className="flex lg:block">
                <div className="w-52 shrink-0 space-y-1.5 rounded-md px-2.5 py-2 lg:w-full">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3.5 w-2/3" />
                </div>
              </li>
            ))}
        </ul>
      </nav>

      {months.state === 'error' && (
        <div className="px-1.5 pb-1.5">
          <SectionError
            variant="inline"
            body={t('rail.error')}
            onRetry={months.retry}
            retrying={months.retrying}
            retryLabel={t('retry')}
          />
        </div>
      )}

      {first && (
        <footer
          className={cn(
            'border-t px-3 py-2.5 text-xs text-muted-foreground',
            firstActive && 'border-l-2 border-l-threadheads-purple bg-primary/10',
          )}
        >
          <p className="max-w-prose">
            {t(first.status === 'paid' ? 'rail.started' : 'rail.startedUnpaid', {
              date: fmt.day(first.month.date),
              amount: fmt.money(first.amountCents, first.currency),
            })}{' '}
            <button
              type="button"
              onClick={() => onSelect(first.token)}
              aria-current={firstActive ? 'true' : undefined}
              aria-label={t('rail.viewFirstAria')}
              className="relative rounded-sm font-medium text-primary-strong underline-offset-2 before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('rail.viewFirst')}
            </button>
          </p>
        </footer>
      )}
    </section>
  );
}
